<template>
  <div class="year-view p-4 grid grid-cols-3 md:grid-cols-4 gap-4">
    <div
      v-for="(month, index) in months"
      :key="index"
      class="month-card border rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
      @click="handleMonthClick(month.date)"
      @dblclick="$emit('dblclick-date', month.date)"
    >
      <div class="month-header bg-gray-50 p-2 text-center font-medium border-b">
        {{ month.name }}
      </div>
      <div class="month-mini-calendar p-2">
        <div class="grid grid-cols-7 gap-1">
          <div
            v-for="day in weekDaysShort"
            :key="day"
            class="text-center text-xs text-gray-500"
          >
            {{ day }}
          </div>
        </div>
        <div class="grid grid-cols-7 gap-1 mt-1">
          <div
            v-for="(day, dayIndex) in getMonthDays(month.date)"
            :key="dayIndex"
            class="mini-day-cell text-center text-xs"
            :class="{
              'text-gray-400': day.month !== 'current',
              'font-bold': day.month === 'current',
              'bg-primary text-white rounded-full': isSelectedDate(day.date),
              'bg-red-100 cursor-not-allowed': isHoliday(day.date),
            }"
          >
            {{ day.dayNumber }}
          </div>
        </div>
      </div>
      <div
        class="month-event-indicator p-2 border-t flex justify-between items-center"
        :class="{ 'bg-gray-50': getEventCountForMonth(month.date) === 0 }"
      >
        <span class="text-xs">
          {{ getEventCountForMonth(month.date) }} events
        </span>
        <div class="flex space-x-1">
          <div
            v-for="status in getStatusesForMonth(month.date)"
            :key="status"
            class="w-2 h-2 rounded-full"
            :class="getStatusColorClass(status)"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from "date-fns";
import { computed } from "vue";

interface Event {
  id: string;
  title: string;
  start: string;
  end?: string;
  status: string;
  type?: string;
  [key: string]: any;
}

interface Holiday {
  id: string;
  name: string;
  date: string;
  type: string;
}

interface CalendarDay {
  date: Date;
  dayNumber: number;
  month: "prev" | "current" | "next";
}

// Define props
const props = defineProps({
  date: {
    type: Date,
    required: true,
  },
  events: {
    type: Array as () => Event[],
    default: () => [],
  },
  holidays: {
    type: Array as () => Holiday[],
    default: () => [],
  },
});

// Define emits
const emit = defineEmits(["month-click", "dblclick-date"]);

const weekDaysShort = computed(() => ["S", "M", "T", "W", "T", "F", "S"]);

const months = computed(() => {
  const result = [];
  const yearStart = startOfYear(props.date);

  for (let i = 0; i < 12; i++) {
    const monthDate = addMonths(yearStart, i);
    result.push({
      date: monthDate,
      name: format(monthDate, "MMMM"),
    });
  }

  return result;
});
const isSelectedDate = (date: Date) => {
  return isSameDay(date, props.date);
};
const getMonthDays = (date: Date) => {
  const days: CalendarDay[] = [];
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  let day = startDate;
  while (day <= endDate) {
    days.push({
      date: new Date(day),
      dayNumber: day.getDate(),
      month: isSameMonth(day, monthStart)
        ? "current"
        : day < monthStart
          ? "prev"
          : "next",
    });
    day = addDays(day, 1);
  }

  return days;
};

const isToday = (date: Date) => {
  return isSameDay(date, new Date());
};

const isHoliday = (date: Date) => {
  return props.holidays.some((holiday) => {
    const holidayDate = parseISO(holiday.date);
    return isSameDay(holidayDate, date);
  });
};

const getEventCountForMonth = (date: Date) => {
  return props.events.filter((event) => {
    const eventDate = parseISO(event.start);
    return isSameMonth(eventDate, date);
  }).length;
};

const getStatusesForMonth = (date: Date) => {
  const events = props.events.filter((event) => {
    const eventDate = parseISO(event.start);
    return isSameMonth(eventDate, date);
  });

  const statuses = new Set<string>();
  events.forEach((event) => {
    if (event.type) {
      statuses.add(event.type);
    } else {
      statuses.add(event.status);
    }
  });

  return Array.from(statuses);
};

const getStatusColorClass = (status: string) => {
  if (status === "reminder") {
    return "bg-purple-500";
  } else if (status === "meeting") {
    return "bg-blue-500";
  } else if (status === "appointment") {
    return "bg-green-500";
  }

  switch (status) {
    case "pending":
      return "bg-yellow-500";
    case "processing":
      return "bg-blue-500";
    case "completed":
      return "bg-green-500";
    case "cancelled":
      return "bg-red-500";
    case "delayed":
      return "bg-orange-500";
    default:
      return "bg-gray-500";
  }
};

const handleMonthClick = (monthDate: Date) => {
  // Find the first event in this month
  const eventInMonth = props.events.find((event) => {
    const eventDate = parseISO(event.start);
    return isSameMonth(eventDate, monthDate);
  });
  // If found, emit that date, else emit the first of the month
  emit("month-click", eventInMonth ? parseISO(eventInMonth.start) : monthDate);
};
</script>

<style scoped>
.mini-day-cell {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
